import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { useLanguage } from "@me8eon/language";
import { EonUiPagination } from "@eon-ui/eon-ui-components-react";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalExportButton from "../components/OnboardingPortalExportButton";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import {
    exportQnADataAsCsv,
    exportQnADataAsExcel,
} from "../OnboardingPortal.export.utils";

const ManageQnAScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        onboardedQnA,
        fetchOnboardedQnA,
        onEditQnA,
        onViewQnA,
        isLoadingOnboardedQnA,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("all");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredQnA = useMemo(
        () =>
            onboardedQnA.filter((qna) => {
                const matchesSearch =
                    qna.question.toLowerCase().includes(search.toLowerCase()) ||
                    qna.answer.toLowerCase().includes(search.toLowerCase());
                const matchesStatus =
                    status === "all" ? true : qna.status === status;
                return matchesSearch && matchesStatus;
            }),
        [search, status, onboardedQnA]
    );

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredQnA.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredQnA]);

    useEffect(() => {
        if (showModule === "manage-qna") {
            fetchOnboardedQnA(jwt);
        }
    }, [showModule]);
    return (
        <>
            <div className="onboarding-portal--content--card">
                {isLoadingOnboardedQnA && <OnboardingPortalLoader />}
                <div className="onboarding-portal--content--card--header">
                    <div className="onboarding-portal--content--card--header--text">
                        {t("onboardingPortal.tableHeaders.qna")} (
                        {filteredQnA.length})
                    </div>

                    <div className="onboarding-portal--content--card--header--actions">
                        <OnboardingPortalRefreshButton
                            onClick={() => fetchOnboardedQnA(jwt)}
                        />
                        <OnboardingPortalSearchFilter
                            t={t}
                            search={search}
                            setSearch={setSearch}
                        />
                        <OnboardingPortalStatusFilter
                            t={t}
                            status={status}
                            setStatus={setStatus}
                        />
                        <OnboardingPortalExportButton
                            t={t}
                            onExportCSV={() =>
                                exportQnADataAsCsv(filteredQnA, "qna_export")
                            }
                            onExportExcel={() =>
                                exportQnADataAsExcel(filteredQnA, "qna_export")
                            }
                        />
                    </div>
                </div>
                <table className="onboarding-portal--table">
                    <OnboardingPortalTableHeaders
                        headers={[
                            "onboardingPortal.tableHeaders.question",
                            "onboardingPortal.tableHeaders.answer",
                            "onboardingPortal.tableHeaders.category",
                            "onboardingPortal.tableHeaders.status",
                            "onboardingPortal.tableHeaders.comments",
                            "onboardingPortal.tableHeaders.action",
                        ]}
                        t={t}
                    />
                    <tbody>
                        {filteredQnA
                            .slice(
                                (currentPage - 1) * pageSize,
                                currentPage * pageSize
                            )
                            .map((qna) => (
                                <tr key={qna.id}>
                                    <td>{qna.question}</td>
                                    <td>{qna.answer}</td>
                                    <td>{qna.category}</td>
                                    <td className={`status--${qna.status}`}>
                                        <span
                                            className={`status--${qna.status}`}
                                        >
                                            {t(
                                                `onboardingPortal.statusLabel.${qna.status}`
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        {qna.status !== "pending"
                                            ? qna.reviewerNote
                                            : "--"}
                                    </td>
                                    <td>
                                        <div className="actions-row">
                                            <span
                                                className="action--view"
                                                onClick={() => onViewQnA(qna)}
                                            >
                                                {t("onboardingPortal.viewBtn")}
                                            </span>
                                            <span
                                                className="action--edit"
                                                onClick={() => onEditQnA(qna)}
                                            >
                                                {t("onboardingPortal.editBtn")}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
            <EonUiPagination
                lang={lang}
                begin={
                    filteredQnA.length === 0 ? 0 : (currentPage - 1) * pageSize
                }
                currentPage={currentPage}
                end={Math.min(currentPage * pageSize, filteredQnA.length)}
                initialPageSize={pageSize}
                pageSize={pageSize}
                pageSizes={"5,10,20,50,100"}
                totalItems={filteredQnA.length}
                onPageChanged={(e) => {
                    setCurrentPage(e.detail as any);
                }}
                onPageSizeChanged={(e) => {
                    setPageSize(e.detail);
                    setCurrentPage(1);
                }}
                onPageDecreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                onPageIncreased={(e) => {
                    console.log(e);
                    setCurrentPage((prev) =>
                        Math.min(
                            prev + 1,
                            Math.ceil(filteredQnA.length / pageSize)
                        )
                    );
                }}
            />
        </>
    );
};

export default ManageQnAScreen;
